import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox, Modal, Radio } from "antd";
import CommonStyles from "@components/CommonStyles";
import { ISurveyQuestion, ISurveyReviewItem } from "@base/modules/checkoutSurvey/checkoutSurvey.interface";
import checkoutSurveyService from "@base/modules/checkoutSurvey/checkoutSurveyService";
import { buildReviewItems, mergeQuestion, parseSurveyPayload, surveyStorage } from "@base/modules/checkoutSurvey/checkoutSurveyRules";

interface IProps {
  onClose: () => void;
  storageId: string;
  /** The `survey` string - from the saved rating, or the one still waiting to be sent. */
  survey?: string;
}

/** The patient's survey answers, read-only. Mounted only while open. */
export default function SurveyReviewModal({ onClose, storageId, survey }: IProps) {
  //! state
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? "vi";

  const [items, setItems] = useState<ISurveyReviewItem[]>(() => surveyStorage.getReview(storageId) ?? []);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  //! function
  // Kept from when the survey was taken on this browser; otherwise rebuilt from the codes alone.
  useEffect(() => {
    if (surveyStorage.getReview(storageId)) {
      return;
    }

    const payload = parseSurveyPayload(survey);

    if (!payload) {
      return;
    }

    (async () => {
      // One loading state for the whole chain, and whatever resolved is shown even if some failed.
      setIsLoading(true);

      try {
        const group = await checkoutSurveyService.getGroup(payload.group, lang);
        const summaries = group.data?.questions ?? [];
        const answered = summaries.filter((question) => question.question_code && payload.answers[question.question_code]);
        const details = await Promise.allSettled(answered.map((question) => checkoutSurveyService.getQuestion(String(question.question_code), lang)));

        const questions: ISurveyQuestion[] = answered.map((summary, index) => {
          const detail = details[index];

          return detail.status === "fulfilled" ? mergeQuestion(detail.value.data, summary) : summary;
        });

        const resolved = buildReviewItems(payload, questions);

        setItems(resolved);

        if (details.every((detail) => detail.status === "fulfilled")) {
          surveyStorage.setReview(storageId, resolved);
        }
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    })();
  }, [storageId, survey, lang]);

  //! render
  return (
    <Modal open onCancel={onClose} footer={null} centered width={640} title={t("checkoutSurvey.viewAnswers")}>
      <div className="relative min-h-[160px] max-h-[65vh] overflow-y-auto flex flex-col gap-4">
        <CommonStyles.SpinnerUI isSpinning={isLoading} />

        {items.map((item, index) => (
          <div key={item.questionCode} className="flex flex-col gap-2">
            <div className="text-common-maintext text-sm font-semibold font-['Inter']">
              {`${t("checkoutSurvey.question")} ${index + 1}: ${item.questionName}`}
            </div>

            {!item.entries.length ? (
              <span className="text-common-subtext text-sm font-['Inter']">{t("checkoutSurvey.notAnswered")}</span>
            ) : (
              item.entries.map((entry, entryIndex) =>
                entry.isFreeText || !entry.name ? (
                  <span key={entryIndex} className="text-common-maintext text-sm font-['Inter']">
                    {`•  ${[entry.name, entry.typed].filter(Boolean).join(": ")}`}
                  </span>
                ) : item.isMultiple ? (
                  <Checkbox key={entryIndex} checked>
                    {entry.name}
                  </Checkbox>
                ) : (
                  <Radio key={entryIndex} checked>
                    {entry.name}
                  </Radio>
                ),
              )
            )}
          </div>
        ))}

        {!isLoading && !items.length ? (
          <span className="text-common-subtext text-sm font-['Inter'] text-center">{t("checkoutSurvey.noData")}</span>
        ) : null}
      </div>
    </Modal>
  );
}
